import { useDispatch, useStore } from 'react-redux';
import { AnyAction, Dispatch } from 'redux';
import { updateCurrentUserPreferencesRequest } from 'store/auth/actions';
import { User } from 'store/auth/models';
import { getAuthUser } from 'store/auth/selectors';
import { StoreState } from 'store/types';

export enum PreferenceActions {
  GET,
  SET,
}
export enum PreferenceSections {
  VIES = 'vies',
  MAIN = 'main',
  ENTITIES = 'entities',
  USERS = 'users',
  TRANSACTIONS = 'transactions',
  EXCEPTIONS = 'exceptions',
  MAPPING = 'mapping',
  RECAP = 'recap',
  VAT_REPORT = 'vatReport',
  APP = 'app',
  FEATURE_DOTS = 'featureDots',
}
export interface Preference {
  name: string;
  value?: any;
}
export interface Preferences {
  section: PreferenceSections;
  prefs: Preference[];
}

function getPreferenceValue(
  preferences: Preferences[],
  section: PreferenceSections,
  name: string
): unknown {
  const sIdx = preferences.findIndex((p) => p.section === section);
  if (sIdx === -1) return undefined;
  const pIdx = preferences[sIdx]?.prefs.findIndex((p) => p.name === name);
  if (pIdx === -1) return undefined;
  return preferences[sIdx].prefs[pIdx].value;
}

function preferencesAreEqual(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (a == null && b == null) return true;
  return JSON.stringify(a) === JSON.stringify(b);
}

// Core preference logic - backend only, no localStorage.
// Reads from currentUser.prefDetails, writes via updateCurrentUserPreferencesRequest.
const preferencesCore = (
  action: PreferenceActions,
  section: PreferenceSections,
  preference: Preference,
  _userId: string,
  currentUser: User | undefined,
  dispatch?: Dispatch
) => {
  const preferences = parseStoredPreferences(currentUser?.prefDetails?.[1]);
  const prefs: Preference[] = [];
  prefs.push(preference);
  if (action === PreferenceActions.GET) {
    if (preferences.length === 0) return undefined;
    const sIdx = preferences.findIndex((p) => p.section === section);
    if (sIdx === -1) return undefined;
    const pIdx = preferences[sIdx]?.prefs.findIndex(
      (p) => p.name === preference.name
    );
    if (pIdx === -1) return undefined;
    return preferences[sIdx].prefs[pIdx].value;
  }
  if (action === PreferenceActions.SET) {
    const existingValue = getPreferenceValue(
      preferences,
      section,
      preference.name
    );
    if (preferencesAreEqual(existingValue, preference.value)) {
      return true;
    }
    applyPreferenceSet(preferences, section, preference);
    if (dispatch) {
      dispatch(updateCurrentUserPreferencesRequest(preferences) as AnyAction);
    }
    return true;
  }
};

function parseStoredPreferences(rawStoredPrefs: unknown): Preferences[] {
  if (!rawStoredPrefs) return [];
  if (typeof rawStoredPrefs === 'string') {
    try {
      return JSON.parse(rawStoredPrefs) as Preferences[];
    } catch (error) {
      console.warn(
        'Unable to parse stored preferences, falling back to empty list.',
        error
      );
      return [];
    }
  }
  if (Array.isArray(rawStoredPrefs)) {
    return rawStoredPrefs as Preferences[];
  }
  return [];
}

function applyPreferenceSet(
  preferences: Preferences[],
  section: PreferenceSections,
  preference: Preference
): void {
  let sIdx = preferences.findIndex((p) => p.section === section);
  if (sIdx === -1) {
    preferences.push({ section, prefs: [] });
    sIdx = preferences.length - 1;
  }
  const pIdx = preferences[sIdx]?.prefs.findIndex(
    (p) => p.name === preference.name
  );
  if (pIdx === -1) {
    preferences[sIdx].prefs.push(preference);
  } else {
    preferences[sIdx].prefs[pIdx] = preference;
  }
}

export const getStoredPreferenceDetails = (state: StoreState): unknown =>
  getAuthUser(state)?.prefDetails?.[1];

/** Apply multiple prefs in one section with a single backend write (avoids SET races). */
export const setPreferencesInSection = (
  section: PreferenceSections,
  updates: Preference[],
  currentUser: User | undefined,
  dispatch: Dispatch,
  getPreferenceDetails?: () => unknown
): void => {
  if (updates.length === 0) return;
  const preferences = parseStoredPreferences(
    getPreferenceDetails?.() ?? currentUser?.prefDetails?.[1]
  );
  let changed = false;
  for (const preference of updates) {
    const existingValue = getPreferenceValue(
      preferences,
      section,
      preference.name
    );
    if (!preferencesAreEqual(existingValue, preference.value)) {
      applyPreferenceSet(preferences, section, preference);
      changed = true;
    }
  }
  if (changed) {
    dispatch(updateCurrentUserPreferencesRequest(preferences) as AnyAction);
  }
};

// Custom hook for use in React components
export const usePreferences = () => {
  const dispatch = useDispatch();
  const store = useStore<StoreState>();

  return (
    action: PreferenceActions,
    section: PreferenceSections,
    preference: Preference,
    userId: string
  ) => {
    const currentUser = getAuthUser(store.getState());
    return preferencesCore(
      action,
      section,
      preference,
      userId,
      currentUser,
      dispatch
    );
  };
};

// Non-hook version for use in reducers and other non-component contexts
export const preferences = (
  action: PreferenceActions,
  section: PreferenceSections,
  preference: Preference,
  userId: string,
  currentUser?: User | undefined,
  dispatch?: Dispatch
) => {
  return preferencesCore(
    action,
    section,
    preference,
    userId,
    currentUser,
    dispatch
  );
};
